import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { A11yModule } from '@angular/cdk/a11y';
import { AvatarComponent } from '../avatar/avatar.component';
import { SectionTitleComponent } from '../section-title/section-title.component';
import { PersonOption } from '../person-select/person-select.component';
import { TestRunHandoverRequest } from '../../models/test-run.model';
import { FIELD_LENGTHS } from '../../shared/field-lengths';

/**
 * Sammel-Postfaecher, an die ein Testlauf statt an eine Person gehen kann
 * (Design-Bundle d1654a822bf, `page-testlauf.jsx:861`).
 *
 * Eigennamen von Organisationseinheiten und keine Oberflaechen-Texte: der gewaehlte
 * Wert wandert unveraendert als `toGroup` ans Backend und steht spaeter so im Banner
 * und in der Uebergabe-Historie. Uebersetzt man ihn, haengt an einem Lauf je nach
 * Sprache des Uebergebenden eine andere Abteilung.
 */
export const HANDOVER_GROUPS: readonly string[] = ['QA-Team', 'DevOps', 'Fachbereich'];

/**
 * Empfaenger und Notiz einer Testlauf-Uebergabe (NEX-1110).
 *
 * <p>Eigene Komponente im FE statt eines Blocks im Runner: dieselbe Uebergabe steht
 * spaeter auch an der Durchlauf-Liste und im Postfach an, und die Regel „genau ein
 * Empfaenger" gehoert dorthin, wo sie gilt.
 *
 * <p>Der Dialog ist selbst gebaut und nutzt nicht {@code lib-modal}. Die Fassung im
 * Monorepo koennte es, die veroeffentlichte nicht — und gebaut wird gegen die
 * veroeffentlichte. In {@code @spirit-testing/component-library@1.24.0}, der Version
 * aus {@code package.json}, kommt keines der noetigen Merkmale vor; nachpruefbar mit
 *
 * <pre>grep -c 'role=\\"dialog\\"\|aria-modal\|cdkTrapFocus\|keydown.escape' \
 *   node_modules/@spirit-testing/component-library/fesm2022/*.mjs</pre>
 *
 * <p>Das Ergebnis ist 0: {@code lib-modal} rendert dort ein nacktes
 * {@code <div class="modal__dialog">}, haengt nicht an {@code @angular/cdk/a11y} und
 * kennt weder {@code subtitle} noch {@code footerTemplate}. Fokusfalle, ESC-Schliessen
 * und {@code role}/{@code aria-modal} verlangt dieses Ticket aber ausdruecklich.
 *
 * <p>Dieselbe Entscheidung traf schon {@code test-case-import-modal};
 * die Library waechst laut CLAUDE-FE nicht aus dem
 * Frontend heraus, ein LIB-Ticket entfaellt darum bewusst.
 *
 * <p>Der Dialog schliesst sich nach der Bestaetigung nicht selbst — er meldet sie nur.
 * Wer ihn oeffnet, haelt den Aufruf offen und meldet ihn ueber {@link submitting}
 * zurueck; sonst verschwaende der Dialog, bevor feststeht, ob die Uebergabe geklappt hat.
 */
@Component({
  selector: 'app-test-run-handover-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, TranslatePipe, MatIconModule, MatProgressSpinnerModule, AvatarComponent, SectionTitleComponent],
  templateUrl: './test-run-handover-modal.component.html',
  styleUrl: './test-run-handover-modal.component.scss',
})
export class TestRunHandoverModalComponent {
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = input.required<boolean>();
  readonly people = input.required<PersonOption[]>();
  readonly groups = input<readonly string[]>(HANDOVER_GROUPS);
  /** Schritt-Bezug des Zwischenstands, z. B. `3/8`; leer, solange keiner feststeht. */
  readonly stepPosition = input<string>('');
  /** Der Aufruf laeuft — der Dialog bleibt stehen und sperrt seine Aktionen. */
  readonly submitting = input<boolean>(false);

  readonly confirmed = output<TestRunHandoverRequest>();
  readonly cancelled = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;

  readonly selectedPersonId = signal<string | null>(null);
  readonly selectedGroup = signal<string | null>(null);
  readonly note = signal('');

  /**
   * Das Backend verlangt genau einen Empfaenger. Steht keiner fest, bleibt die
   * Bestaetigung gesperrt, statt den Fehler erst vom Server zu holen.
   */
  readonly canSubmit = computed(
    () => (this.selectedPersonId() !== null || this.selectedGroup() !== null) && !this.submitting(),
  );

  readonly subtitle = computed(() => {
    const position = this.stepPosition();
    return position
      ? this.translate.instant('testRunner.handover.subtitleAtStep', { step: position })
      : this.translate.instant('testRunner.handover.subtitle');
  });

  private triggerElement: HTMLElement | null = null;

  /**
   * Oeffnen und Schliessen: die Seite
   * dahinter scrollt nicht mit, und der Fokus kehrt zu dem Knopf zurueck, der den
   * Dialog geoeffnet hat — sonst steht er nach dem Schliessen am Seitenanfang.
   *
   * <p>Ein erneutes Oeffnen faengt leer an: die Auswahl von vorhin galt der Uebergabe
   * von vorhin, und eine stehengebliebene Notiz ginge sonst ungeprueft mit.
   */
  constructor() {
    let wasVisible = false;
    effect(() => {
      const open = this.visible();
      if (open && !wasVisible) {
        this.triggerElement = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
        this.reset();
      } else if (!open && wasVisible) {
        document.body.style.overflow = '';
        this.triggerElement?.focus();
        this.triggerElement = null;
      }
      wasVisible = open;
    });
    this.destroyRef.onDestroy(() => (document.body.style.overflow = ''));
  }

  selectPerson(id: string): void {
    this.selectedPersonId.set(id);
    this.selectedGroup.set(null);
  }

  toggleGroup(group: string): void {
    const next = this.selectedGroup() === group ? null : group;
    this.selectedGroup.set(next);
    if (next !== null) this.selectedPersonId.set(null);
  }

  submit(): void {
    if (!this.canSubmit()) return;
    const note = this.note().trim();
    const group = this.selectedGroup();
    this.confirmed.emit({
      ...(group !== null ? { toGroup: group } : { toUserId: this.selectedPersonId() as string }),
      ...(note ? { note } : {}),
    });
  }

  cancel(): void {
    if (this.submitting()) return;
    this.cancelled.emit();
  }

  private reset(): void {
    this.selectedPersonId.set(null);
    this.selectedGroup.set(null);
    this.note.set('');
  }
}
